import { useQuery, UseQueryResult, useQueryClient, UseMutationResult, useMutation } from '@tanstack/react-query';
import {
  getStructuresByTypeCategoryCode,
  getStructuresByTypeCategoryCodeAndTypeCode,
  createStructure,
  getNodeChildrenByNodeType,
  getNodeAttributesByCustomRange,
  getNodeById,
  updateStructure,
  getNodeChildren,
  updateStructureBasicData,
  getNodeSubTree,
  getNodeFilteredAttributesByCustomRange,
  getParentAttributesByPropertyType,
  getPrioritySelection,
  getSuggestgedNodesFromAllocationEngine,
  getSiteSchemasByClientContract,
  getAllPartsForStructures,
  getNodeByName,
  getAllSiteSchemas,
  postSiteSchema,
  updateSiteSchema,
  getSiteSchema,
  getLoggedInSiblingParentDetails,
  getAllShipmentsForStructures,
  getDestinationAddressByNodeId,
  getNodeAttributesByPropertyTypeAndNodeIds,
  getNodeAttributesByNodeId,
  getAllNodesByNodeType,
  mergeStructure,
  getDispatchDestinationSuggestions,
  getNodeAttributeById,
  getAllocationNodeParentData,
  getCommonFleetTagsByPartTypeIds,
  checkMultipleServiceCreationAccess,
  childLocationCountGroupedByNodeType
} from '../api/structures';
import { LocationNodeData, PaginationSettings, StructureResponse, SiteSchemaDropdown, SiblingParentDetails, NodeAttributesReponse, AddressAttributeResponseType, NodeData, DispatchDestinationNodeType, FaultCode, QueryOption } from '../@types';
import { uploadFiles } from '../api/uploadedFiles';
import { StructurePartListType } from '../pages/Structures/AddEditStructureLocation/PartsAccordianFragment';
import {
  FilteredNodeAttributesByCustomRangeKey,
  NodeAttributesByCustomRangeKey,
  NodeByName,
  ParentAttributesByPropertyTypeKey,
  PrioritySelectionObjectTypeKey,
  SiteSchemaAllKey,
  SiteSchemaOneKey,
  StructureChildrenKey,
  StructureKey,
  StructureSubTreeKey,
  StructureSiteSchemaKey,
  PartsForStructuresKey,
  SiblingParentDetailsKey,
  ShipmentsForStructuresKey,
  NodeAttributesByPropertyTypeAndNodeIdsKey,
  NodeAttributesByNodeIdKey,
  NodeByTypeKey,
  NodesAllocationByNode,
  NodesAllocationByService,
  ShipmentDestinationNodesKey,
  NodeAttributeByIdKey,
  NodeAllocationParentByIdKey,
  MultipleServiceCreationKey,
  ChildLocationCountKey,
} from '../configs/structures';
import { Folders } from '../@types/uploaded-files.type';
import { StructureShipmentListType } from '../pages/Structures/AddEditStructureLocation/ShipmentAccordianFragment';
import { ColumnSelectionItem } from '../components/templates/ColumnSelection';
import { getSelectedColumnNames } from '../utils/columnSelectionOperations';

export interface StructureListResponse {
  previousPage?: number;
  currentPage?: number;
  nextPage?: number;
  total?: number;
  limit?: number;
  data?: [any];
}

export interface LogoPicUploadData {
  id: string;
  logoPic?: string;
}

export const useGetStructurePaginatedDataByType = (catagoryCode: string, queryOption: QueryOption, columnSelectionItems?: ColumnSelectionItem[], defaultExcludedColumns: string[] = []): UseQueryResult<StructureListResponse> => {
  return useQuery({
    queryKey: [StructureKey, queryOption],
    queryFn: () => getStructuresByTypeCategoryCode(catagoryCode, { ...queryOption, excludedColumns: getSelectedColumnNames(columnSelectionItems, defaultExcludedColumns) }),
    gcTime: 0,
    enabled: false
  });
};

export const useGetStructuresByTypeCategoryCodeAndTypeCode = (catagoryCode: string, typeCode: string): UseQueryResult<StructureResponse[]> => {
  return useQuery({
    queryKey: [StructureKey, catagoryCode, typeCode],
    queryFn: () => getStructuresByTypeCategoryCodeAndTypeCode(catagoryCode, typeCode),
    enabled: false
  });
};

export const useGetStructuresByTypeCategoryCodeAndTypeCodeForReplenishment = (catagoryCode: string, typeCode: string): UseQueryResult<StructureResponse[]> => {
  return useQuery({
    queryKey: [StructureKey, catagoryCode, typeCode],
    queryFn: () => getStructuresByTypeCategoryCodeAndTypeCode(catagoryCode, typeCode)
  });
};

export const useGetNodeChildrenByNodeType = (nodeId: string, nodeTypeCode: string): UseQueryResult<StructureResponse[]> => {
  return useQuery({
    queryKey: [StructureChildrenKey, nodeId, nodeTypeCode],
    queryFn: () => nodeId && nodeId !== '0' && getNodeChildrenByNodeType(nodeId, nodeTypeCode),
    enabled: false
  });
};

export const useGetNodeChildrenByNodeTypeAsEnabled = (): UseMutationResult<StructureResponse[]> => {
  return useMutation({
    mutationFn: async (data: any) => {
      return await getNodeChildrenByNodeType(data.nodeId, data.nodeTypeCode);
    }
  });
};

export const useGetNodeChildren = (nodeId: string, includeInactiveChildren = false): UseQueryResult<StructureResponse[]> => {
  return useQuery({
    queryKey: [StructureChildrenKey, nodeId],
    queryFn: () => getNodeChildren(nodeId, includeInactiveChildren),
    enabled: false
  });
};

export const useCreateStructure = (): UseMutationResult<any, any, any, any> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LogoPicUploadData) => {
      const createResponse = await createStructure(data);

      if (createResponse && createResponse.id && data.logoPic) {
        const formData = new FormData();
        const files = data.logoPic;
        formData.append('fileList', files[0]);
        await uploadFiles(createResponse.id, formData, Folders.ClientContractLogos);
      }

      return createResponse;
    },

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [StructureKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [StructureChildrenKey, `${response.parentNodeId}`, `${response.nodeTypeCode}`]
      });
    }
  });
};

export const useGetFilteredNodeAttributesByCustomRange = (nodeId: string, nodeAttributes: any, serviceTypeCode: string, faultType?: string, actionTypeCode?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: [FilteredNodeAttributesByCustomRangeKey, nodeId, nodeAttributes, serviceTypeCode, actionTypeCode],

    queryFn: () =>
      getNodeFilteredAttributesByCustomRange(nodeId, nodeAttributes, serviceTypeCode, faultType, actionTypeCode),

    enabled: false
  });
};

export const useGetNodeAttributesByCustomRange = (nodeId: string, nodeAttributes: any, visibilityBypassServiceId?: number): UseQueryResult<any> => {
  return useQuery({
    queryKey: [NodeAttributesByCustomRangeKey, nodeId, nodeAttributes],
    queryFn: () => nodeId !== '0' && getNodeAttributesByCustomRange(nodeId, nodeAttributes, visibilityBypassServiceId),
    enabled: false
  });
};

export const useGetNodeAttributesByCustomRangeForReplenishment = (nodeId: string, nodeAttributes: any): UseQueryResult<any> => {
  return useQuery({
    queryKey: [NodeAttributesByCustomRangeKey, nodeId, nodeAttributes],
    queryFn: () => getNodeAttributesByCustomRange(nodeId, nodeAttributes)
  });
};

export const useGetNodeByName = (name: string, nodeTypeCode: string, parentNodeId?: number): UseQueryResult<any> => {
  return useQuery({
    queryKey: [NodeByName, name, nodeTypeCode, parentNodeId || 0],
    queryFn: () => name && nodeTypeCode && getNodeByName(name, nodeTypeCode, parentNodeId)
  });
};

export const useUpdateStructure = (): UseMutationResult<any, any, any, any> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LogoPicUploadData) => {
      if (data.logoPic) {
        const formData = new FormData();
        const files = data.logoPic;
        formData.append('fileList', files[0]);
        await uploadFiles(Number(data.id), formData, Folders.ClientContractLogos);
      }

      return await updateStructure(data.id, data);
    },

    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: [StructureKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [StructureKey, `${data.id}`]
      });
      await queryClient.invalidateQueries({
        queryKey: [StructureSubTreeKey, `${data.id}`]
      });
      await queryClient.invalidateQueries({
        queryKey: [StructureChildrenKey, `${data.parentNodeId}`, `${data.nodeTypeCode}`]
      });
    }
  });
};

export const useUpdateStructureBasicData = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await updateStructureBasicData(data.id, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [StructureKey]
      });
    }
  });
};

export const useGetNodeById = (nodeId: string, nodeTypeCategoryCode?: string): UseQueryResult<StructureResponse> => {
  return useQuery({
    queryKey: [StructureKey, nodeId],
    queryFn: async () => nodeId !== 'create' && getNodeById(nodeId, nodeTypeCategoryCode),
    enabled: false
  });
};

export const useGetNodeSubTree = (nodeId: string): UseQueryResult<StructureResponse[]> => {
  return useQuery({
    queryKey: [StructureSubTreeKey, nodeId],
    queryFn: () => nodeId !== '0' && getNodeSubTree(nodeId),
    enabled: false
  });
};

export const useGetParentAttributesByPropertyType = (nodeId: string, propertyTypeCode: string, visibilityBypassServiceId?: number): UseQueryResult<any[]> => {
  return useQuery({
    queryKey: [ParentAttributesByPropertyTypeKey, nodeId, propertyTypeCode],
    queryFn: () => getParentAttributesByPropertyType(nodeId, propertyTypeCode, visibilityBypassServiceId),
    enabled: false
  });
};

export const useGetPrioritySelection = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await getPrioritySelection(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PrioritySelectionObjectTypeKey]
      });
    }
  });
};

export const useGetSuggestedNodesFromAllocationEngine = (serviceTypeCode: string, actionTypeCode: string, processTypeCode: string, brandId?: number, mainLocationId?: number, serviceId?: number): UseQueryResult<LocationNodeData[]> => {
  return useQuery({
    queryKey: serviceId ? [NodesAllocationByService, serviceId] : [NodesAllocationByNode, serviceTypeCode, actionTypeCode, processTypeCode, mainLocationId],
    queryFn: () => serviceTypeCode && actionTypeCode && mainLocationId && getSuggestgedNodesFromAllocationEngine(serviceTypeCode, actionTypeCode, processTypeCode, brandId, mainLocationId, serviceId),
    enabled: false
  });
};

export const useGetSiteSchemasByClientContract = (clientId: string, contractId: string): UseQueryResult<SiteSchemaDropdown[]> => {
  return useQuery({
    queryKey: [StructureSiteSchemaKey, contractId],
    queryFn: () => getSiteSchemasByClientContract(clientId, contractId),
    enabled: false
  });
};

export const useGetAllPartsForStructures = (pageSettings: PaginationSettings, nodeId: number, isEnableToShowChildrenParts: boolean): UseQueryResult<StructurePartListType> => {
  return useQuery({
    queryKey: [PartsForStructuresKey, pageSettings.currentPage, pageSettings.limit, nodeId, isEnableToShowChildrenParts],
    queryFn: async () => getAllPartsForStructures(pageSettings.limit, pageSettings.currentPage, nodeId, isEnableToShowChildrenParts),
    gcTime: 0
  });
};

export const useGetAllSiteSchemas = (nodeId: number) => {
  return useQuery({
    queryKey: [SiteSchemaAllKey, nodeId],
    queryFn: () => getAllSiteSchemas(nodeId)
  });
};

export const useGetSiteSchema = (siteSchemaId: number) => {
  return useQuery({
    queryKey: [SiteSchemaOneKey, siteSchemaId],
    queryFn: () => getSiteSchema(siteSchemaId)
  });
};

export const useCreateSiteSchema = (nodeId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await postSiteSchema(data);
    },

    onSuccess: async (response: any) => {
      await queryClient.invalidateQueries({
        queryKey: [SiteSchemaOneKey, response.id]
      });
      await queryClient.invalidateQueries({
        queryKey: [SiteSchemaAllKey, nodeId]
      });
    }
  });
};

export const useUpdateSiteSchema = (nodeId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await updateSiteSchema(data);
    },

    onSuccess: async (response: any) => {
      await queryClient.invalidateQueries({
        queryKey: [SiteSchemaOneKey, response.id]
      });
      await queryClient.invalidateQueries({
        queryKey: [SiteSchemaAllKey, nodeId]
      });
    }
  });
};

export const useGetLoggedInSiblingParentDetails = (returnImmediateParentNode?: boolean): UseQueryResult<SiblingParentDetails> => {
  return useQuery({
    queryKey: [SiblingParentDetailsKey],
    queryFn: () => getLoggedInSiblingParentDetails(returnImmediateParentNode),
    enabled: false
  });
};

export const useGetAllShipmentsForStructures = (pageSettings: PaginationSettings, nodeId: number, isEnableToShowChildrenShipments: boolean): UseQueryResult<StructureShipmentListType> => {
  return useQuery({
    queryKey: [ShipmentsForStructuresKey, pageSettings.currentPage, pageSettings.limit, nodeId, isEnableToShowChildrenShipments],
    queryFn: async () => getAllShipmentsForStructures(pageSettings.limit, pageSettings.currentPage, nodeId, isEnableToShowChildrenShipments),
    gcTime: 0
  });
};

export const useGetDestinationAddressByNodeId = (nodeId: string): UseQueryResult<AddressAttributeResponseType[]> => {
  return useQuery({
    queryKey: [ShipmentsForStructuresKey, nodeId],
    queryFn: () => getDestinationAddressByNodeId(nodeId),
    enabled: false
  });
};

export const useGetNodeAttributesByPropertyTypeAndNodeIds = (propertyTypeCode: string, nodeIds: string): UseQueryResult<NodeAttributesReponse[]> => {
  return useQuery({
    queryKey: [NodeAttributesByPropertyTypeAndNodeIdsKey, propertyTypeCode, nodeIds],
    queryFn: () => getNodeAttributesByPropertyTypeAndNodeIds(propertyTypeCode, nodeIds)
  });
};

export const useGetNodeAttributesByNodeId = (nodeId: number): UseQueryResult<NodeAttributesReponse[]> => {
  return useQuery({
    queryKey: [NodeAttributesByNodeIdKey, nodeId],
    queryFn: () => nodeId && getNodeAttributesByNodeId(nodeId)
  });
};

export const useGetAllNodesByNodeType = (nodeTypeCode: string): UseQueryResult<NodeData[]> => {
  return useQuery({
    queryKey: [NodeByTypeKey, nodeTypeCode],
    queryFn: () => getAllNodesByNodeType(nodeTypeCode)
  });
};

export const useMergeStructure = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await mergeStructure(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [StructureKey]
      });
    }
  });
};

export const useGetDispatchDestinationSuggestions = (serviceId: number, mainLocationNodeId: number, subLocationNodeId: number, primaryAllocationNodeId: number, secondaryAllocationNodeId: number): UseQueryResult<DispatchDestinationNodeType[]> => {
  return useQuery({
    queryKey: [ShipmentDestinationNodesKey, serviceId],
    queryFn: () => serviceId && getDispatchDestinationSuggestions(serviceId, mainLocationNodeId, subLocationNodeId, primaryAllocationNodeId, secondaryAllocationNodeId),
    enabled: false
  });
};

export const useGetNodeAttributeById = (nodeAttributeId: number): UseQueryResult<FaultCode> => {
  return useQuery({
    queryKey: [NodeAttributeByIdKey, nodeAttributeId],
    queryFn: () => nodeAttributeId !== -1 && getNodeAttributeById(nodeAttributeId)
  });
};

export const useGetAllocationNodeParentData = (nodeId?: number): UseQueryResult<NodeData> => {
  return useQuery({
    queryKey: [NodeAllocationParentByIdKey, nodeId],
    queryFn: () => nodeId && getAllocationNodeParentData(nodeId),
    enabled: false
  });
};

export const useGetCommonFleetTagsByPartTypeIds = (): UseMutationResult<{ id: number; name: string }[]> => {
  return useMutation({
    mutationFn: async (data: any) => {
      return await getCommonFleetTagsByPartTypeIds(data.partTypeIds);
    }
  });
};

export const useCheckMultipleServiceCreationAccess = (nodeId: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: [MultipleServiceCreationKey, nodeId],
    queryFn: () => checkMultipleServiceCreationAccess(nodeId),
    enabled: false
  });
};

export const useChildLocationCountGroupedByNodeType = (locationId: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: [ChildLocationCountKey, locationId],
    queryFn: () => childLocationCountGroupedByNodeType(locationId),
    enabled: false
  });
};